// src/components/reports/ReportCard.tsx
import React from 'react';
import { FileText, Download, Printer, Table } from 'lucide-react';

interface ReportCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onGenerate: () => void;
  onDownloadPDF: () => void;
  onDownloadExcel: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode; // For filters
}

const ReportCard: React.FC<ReportCardProps> = ({
  title,
  description,
  icon,
  onGenerate,
  onDownloadPDF,
  onDownloadExcel,
  isLoading = false,
  disabled = false,
  children,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
            {icon}
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">{description}</p>
          
          {/* Filter Controls */}
          {children && (
            <div className="mb-4 p-4 bg-gray-50 rounded-md">
              {children}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onGenerate}
              disabled={disabled || isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="h-4 w-4 mr-2" />
              {isLoading ? 'Generating...' : 'Generate Report'}
            </button>
            
            <button
              onClick={onDownloadPDF}
              disabled={disabled || isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="h-4 w-4 mr-2" />
              PDF
            </button>
            
            <button
              onClick={onDownloadExcel}
              disabled={disabled || isLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Table className="h-4 w-4 mr-2" />
              Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportCard;
